import { useEffect, useState } from 'react';
import { Building2, UserPlus, Loader2, AlertCircle, CheckCircle, Trash2, Loader, Check, X } from 'lucide-react';
import { organizationApi } from '../services/api';
import { useAuth } from '../stores/authStore';
import { toast, confirmDialog } from '../stores/feedbackStore';
import { formatDateTime } from '@pdfsign/shared/utils';

const roleLabels: Record<string, string> = {
  owner: 'Chủ sở hữu',
  admin: 'Quản trị',
  editor: 'Biên tập',
  signer: 'Người ký',
  viewer: 'Xem',
  member: 'Thành viên',
};

interface Member {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  joined_at: string | null;
  invited_at: string;
}

export default function OrganizationPage() {
  const { user } = useAuth();
  const [org, setOrg] = useState<any>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ full_name: '', email: '', role: 'signer' });
  const [orgForm, setOrgForm] = useState({ name: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user?.organization_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [orgRes, memberRes] = await Promise.all([
        organizationApi.get(user.organization_id),
        organizationApi.listMembers(user.organization_id, { limit: 100 }),
      ]);
      setOrg(orgRes.data.data);
      setMembers(memberRes.data.data);
      setOrgForm({ name: orgRes.data.data.name });
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tải thông tin tổ chức');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user?.organization_id]);

  const isAdmin = ['owner', 'admin'].includes(org?.user_role || '');

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    setError(null);
    try {
      await organizationApi.inviteMember(org.id, inviteForm);
      setShowInvite(false);
      setInviteForm({ full_name: '', email: '', role: 'signer' });
      setSuccess('Đã thêm thành viên vào tổ chức');
      load();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể mời thành viên');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await organizationApi.update(org.id, { name: orgForm.name });
      setSuccess('Đã cập nhật tổ chức');
      load();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể cập nhật');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (memberId: string) => {
    const ok = await confirmDialog({
      title: 'Xóa thành viên khỏi tổ chức',
      message: 'Bạn có chắc chắn muốn xóa thành viên này khỏi tổ chức?',
      confirmText: 'Xóa thành viên',
      cancelText: 'Hủy',
      type: 'danger',
    });
    if (!ok) return;

    try {
      await organizationApi.removeMember(org.id, memberId);
      toast.success('Đã xóa thành viên khỏi tổ chức thành công');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Không thể xóa thành viên');
    }
  };

  if (loading) {
    return <div className="page-container"><div className="card p-10 text-center text-gray-500">Đang tải...</div></div>;
  }

  if (!user?.organization_id) {
    return (
      <div className="page-container">
        <div className="card p-12 text-center">
          <Building2 className="w-14 h-14 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Bạn chưa thuộc tổ chức nào</h3>
          <p className="text-gray-500">Liên hệ quản trị viên để được thêm vào tổ chức.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 animate-fade-in">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-slate-200/80 dark:border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-sky-50 dark:bg-sky-500/10 border border-sky-200/60 dark:border-sky-500/20 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-medium tracking-tight text-slate-900 dark:text-white">
              Tổ chức & Tài khoản
            </h1>
            <p className="text-xs text-slate-400 font-normal mt-0.5">
              Quản lý thông tin tổ chức, thành viên và phân quyền tài khoản ký số
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-2.5 text-rose-600 dark:text-rose-400 text-xs font-normal">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
      {success && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-2.5 text-emerald-600 dark:text-emerald-400 text-xs font-normal">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Org Info (4 cols) */}
        <div className="lg:col-span-4 card p-4 sm:p-5">
          <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-3.5 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-sky-500" />
            <span>Thông tin đơn vị</span>
          </h2>
          <form onSubmit={handleUpdateOrg} className="space-y-3.5">
            <div>
              <label className="label text-xs">Tên tổ chức / Đơn vị</label>
              <input
                value={orgForm.name}
                onChange={(e) => setOrgForm({ name: e.target.value })}
                className="input text-xs"
                disabled={!isAdmin}
              />
            </div>
            <div>
              <label className="label text-xs">Mã định danh (Slug)</label>
              <input value={org?.slug || ''} className="input text-xs bg-slate-50 dark:bg-white/[0.03]" disabled />
            </div>
            <div className="p-3 bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/5 rounded-xl text-xs text-slate-400 space-y-1">
              <p>Tổng nhân sự: <strong className="text-slate-700 dark:text-slate-200 font-medium">{org?.member_count || members.length} tài khoản</strong></p>
              <p>Quyền hiện tại: <span className="text-sky-500 font-medium">{roleLabels[org?.user_role] || org?.user_role}</span></p>
            </div>
            {isAdmin && (
              <button type="submit" disabled={saving} className="btn-primary w-full text-xs h-8 font-medium">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Lưu thay đổi'}
              </button>
            )}
          </form>
        </div>

        {/* Member List (8 cols) */}
        <div className="lg:col-span-8 card overflow-hidden">
          <div className="py-2.5 px-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between bg-slate-50/50 dark:bg-white/[0.02]">
            <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              Danh sách tài khoản thành viên ({members.length})
            </h2>
            {isAdmin && (
              <button
                onClick={() => setShowInvite(true)}
                className="btn-primary text-xs py-1 px-2.5 inline-flex items-center gap-1 font-medium h-7"
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>Thêm thành viên</span>
              </button>
            )}
          </div>
          <div className="divide-y divide-slate-100 dark:divide-white/5 max-h-[30rem] overflow-y-auto">
            {members.map((m) => (
              <div key={m.id} className="p-3.5 flex items-center gap-3 hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-500/15 border border-sky-200 dark:border-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-600 dark:text-sky-400 text-xs font-medium">
                    {m.full_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">{m.full_name}</p>
                  <p className="text-[11px] text-slate-400 font-mono truncate">{m.email}</p>
                </div>
                <span className="badge badge-gray text-[10px] font-normal">
                  {roleLabels[m.role] || m.role}
                </span>
                {m.status === 'pending' && <span className="badge badge-warning text-[10px] font-normal">Chờ duyệt</span>}
                {isAdmin && m.role !== 'owner' && (
                  <button
                    onClick={() => handleRemove(m.id)}
                    className="p-1.5 text-slate-400 hover:text-rose-500 rounded transition-colors"
                    title="Xóa tài khoản khỏi tổ chức"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowInvite(false)}>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
              <h2 className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-sky-500" />
                <span>Thêm tài khoản vào tổ chức</span>
              </h2>
              <button onClick={() => setShowInvite(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <span className="sr-only">Đóng</span>
                ✕
              </button>
            </div>
            <form onSubmit={handleInvite} className="p-5 space-y-3.5">
              <div>
                <label className="label text-xs font-normal">Họ và tên thành viên <span className="text-rose-500">*</span></label>
                <input
                  required
                  type="text"
                  placeholder="Ví dụ: Huỳnh Thị Thu Hằng"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  className="input text-xs font-normal"
                />
              </div>
              <div>
                <label className="label text-xs font-normal">Email tài khoản <span className="text-rose-500">*</span></label>
                <input
                  required
                  type="email"
                  placeholder="hang.huynh@coquan.gov.vn"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="input text-xs font-normal"
                />
              </div>
              <div>
                <label className="label text-xs font-normal">Vai trò & phân quyền</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="input text-xs font-normal"
                >
                  <option value="signer">Người ký (Signer) — Ký tài liệu được giao</option>
                  <option value="editor">Biên tập viên (Editor) — Được tạo và gửi tài liệu ký</option>
                  <option value="admin">Quản trị viên (Admin) — Toàn quyền quản trị</option>
                  <option value="viewer">Chỉ xem (Viewer) — Chỉ xem hồ sơ</option>
                </select>
              </div>
              <div className="flex gap-2.5 pt-2">
                <button type="button" onClick={() => setShowInvite(false)} className="btn-outline flex-1 text-xs h-8 font-normal flex items-center justify-center gap-1.5">
                  <X className="w-3.5 h-3.5" />
                  <span>Hủy</span>
                </button>
                <button type="submit" disabled={inviting} className="btn-primary flex-1 text-xs h-8 font-medium flex items-center justify-center gap-1.5">
                  {inviting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Thêm</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}